import { Add, CardMembership, CheckCircle, Close, Refresh } from '@mui/icons-material';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EAnalysisStatus, type ICertificateSelectionUIData } from '@sharedInterfaces/IEmployeeTalkState';
import type { ICertificateSelectionComponentProperties } from '@sharedInterfaces/IEmployeeTalkTemplate';
import { useMapCertificateDialog } from '@src/App/Components/Dialogs/MapCertificateDialog/MapCertificateDialog';
import RowElement from '@src/App/Components/General/FormularElements/RowElement/RowElement';
import type { AppState } from '@store/store';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ITemplateComponentProps } from '../TemplateEditor/types';
import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';
import { useEmployeeTalk } from './ETTEmployeeTalkProvider';

const ETTCertificateSelectionComponent: React.FC<ITemplateComponentProps> = (props) => {
    const theme = useTheme();
    const allCertificates = useSelector((state: AppState) => state.company.allCertificates);
    const properties = props.component.properties as ICertificateSelectionComponentProperties;
    const mapCertificateDialog = useMapCertificateDialog();

    const { secureState, selectCertificate, deselectCertificate, regenerateCertificates, isReadOnlyForCurrentUser, isManualMode } =
        useEmployeeTalk();

    const cvAnalysis = {
        state: {
            isAnalyzing: secureState?.analysis.certificates.status === EAnalysisStatus.LOADING,
            results: {
                extractedCertificates: secureState?.analysis.certificates.data || [],
            },
            error:
                secureState?.analysis.certificates.status === EAnalysisStatus.ERROR
                    ? 'CV-Analyse fehlgeschlagen'
                    : undefined,
        },
    };

    const [acceptedItems, setAcceptedItems] = useState<{
        certificates: Array<{
            title: string;
            selected: boolean;
            id?: number;
            issuer?: string;
            startDate?: string;
            endDate?: string;
        }>;
    }>({ certificates: [] });

    const [expandedForm, setExpandedForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [inlineForm, setInlineForm] = useState({ name: '', startDate: '', endDate: '' });
    const [isRegenerating, setIsRegenerating] = useState(false);

    useEffect(() => {
        const componentData = props.value as ICertificateSelectionUIData | undefined;
        if (componentData?.certificates) {
            setAcceptedItems({ certificates: componentData.certificates });
        }
    }, [props.value]);

    const handleAcceptCertificate = async (certTitle: string) => {
        const cert = cvAnalysis.state.results?.extractedCertificates?.find((c) => c.title === certTitle);

        try {
            const result = await mapCertificateDialog(cert?.id, certTitle, 'datesOnly');

            if (result.success) {
                setAcceptedItems((prev) => ({
                    certificates: prev.certificates.map((c) => (c.title === certTitle ? { ...c, selected: true } : c)),
                }));

                await selectCertificate(certTitle, result.startDate, result.endDate, result.durationInDays);
            }
        } catch (error) {
            console.error('Failed to accept certificate:', error);
        }
    };

    const handleDeselectCertificate = async (certTitle: string) => {
        setAcceptedItems((prev) => ({
            certificates: prev.certificates.map((c) => (c.title === certTitle ? { ...c, selected: false } : c)),
        }));

        try {
            await deselectCertificate(certTitle);
        } catch (err) {
            console.error('Failed to deselect certificate:', err);
        }
    };

    const handleAddCertificate = async () => {
        if (!inlineForm.name.trim()) {
            setError('Bitte wählen Sie ein Zertifikat aus');
            return;
        }
        setLoading(true);

        try {
            const existingCert = allCertificates.find((c) => c.title === inlineForm.name);
            const result = await mapCertificateDialog(existingCert?.id, inlineForm.name, 'datesOnly');

            if (result.success) {
                await selectCertificate(inlineForm.name, result.startDate, result.endDate, result.durationInDays);
                setInlineForm({ name: '', startDate: '', endDate: '' });
                setExpandedForm(false);
            }
        } catch (error) {
            console.error('Failed to add certificate:', error);
            setError('Fehler beim Hinzufügen des Zertifikats');
        }
        setLoading(false);
    };

    const renderAnalysisResults = () => {
        const { results } = cvAnalysis.state;
        if (!results) return null;

        const suggestedCertTitles = new Set((results.extractedCertificates || []).map((c) => c.title));
        const manuallyAddedCerts = acceptedItems.certificates.filter((cert) => !suggestedCertTitles.has(cert.title));

        const availableCertificates = allCertificates.filter(
            (cert) => !suggestedCertTitles.has(cert.title) && !manuallyAddedCerts.some((m) => m.title === cert.title),
        );

        return (
            <Card sx={{ mb: 3 }}>
                <CardHeader
                    title={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                                sx={{
                                    width: 32,
                                    height: 32,
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                }}
                            >
                                <CardMembership fontSize="small" />
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 400 }}>
                                Erkannte Zertifikate
                            </Typography>
                        </Box>
                    }
                    sx={{ borderBottom: `1px solid ${theme.palette.divider}`, py: 2 }}
                />
                <CardContent>
                    <Alert
                        severity="info"
                        sx={{
                            mb: 2,
                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                            border: `1px solid ${theme.palette.info.main}`,
                        }}
                    >
                        <Typography variant="body1" sx={{ fontSize: '0.9rem', color: theme.palette.info.main }}>
                            Wähle Zertifikate aus, die du besitzt. Du kannst weitere Zertifikate hinzufügen.
                        </Typography>
                    </Alert>

                    {results.extractedCertificates && results.extractedCertificates.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ color: 'text.primary', fontWeight: 500, mb: 1.5, fontSize: '1.1rem' }}
                            >
                                Aus CV extrahiert
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Zertifikat
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Aussteller
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Aktion
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {results.extractedCertificates.map((cert) => {
                                            const acceptedCert = acceptedItems.certificates.find(
                                                (c) => c.title === cert.title,
                                            );
                                            const isAccepted = acceptedCert?.selected === true;
                                            return (
                                                <TableRow
                                                    key={cert.title}
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2">{cert.title}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {cert.issuer || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box
                                                            sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}
                                                        >
                                                            {!isAccepted && (
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    disabled={isReadOnlyForCurrentUser}
                                                                    sx={{
                                                                        color: '#0BB140',
                                                                        borderColor: '#0BB140',
                                                                        '&:hover': {
                                                                            backgroundColor: '#0BB140',
                                                                            color: 'white',
                                                                        },
                                                                    }}
                                                                    onClick={() => handleAcceptCertificate(cert.title)}
                                                                >
                                                                    ✓
                                                                </Button>
                                                            )}
                                                            {isAccepted && (
                                                                <Chip
                                                                    icon={<CheckCircle />}
                                                                    label="✓"
                                                                    size="small"
                                                                    onClick={
                                                                        isReadOnlyForCurrentUser
                                                                            ? undefined
                                                                            : () =>
                                                                                  handleDeselectCertificate(cert.title)
                                                                    }
                                                                    sx={{
                                                                        backgroundColor: '#0BB140',
                                                                        color: 'white',
                                                                        cursor: isReadOnlyForCurrentUser
                                                                            ? 'default'
                                                                            : 'pointer',
                                                                        '&:hover': isReadOnlyForCurrentUser
                                                                            ? {}
                                                                            : { backgroundColor: '#0a9e38' },
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {manuallyAddedCerts.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ color: 'text.primary', fontWeight: 500, mb: 1.5, fontSize: '1.1rem' }}
                            >
                                Manuell hinzugefügt
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.secondary.main,
                                                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.secondary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Zertifikat
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.secondary.main,
                                                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.secondary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Aussteller
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.secondary.main,
                                                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.secondary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Aktion
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {manuallyAddedCerts.map((cert) => {
                                            const isAccepted = cert.selected === true;
                                            return (
                                                <TableRow
                                                    key={cert.title}
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2">{cert.title}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {cert.issuer || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box
                                                            sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}
                                                        >
                                                            {!isAccepted && (
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    disabled={isReadOnlyForCurrentUser}
                                                                    sx={{
                                                                        color: '#0BB140',
                                                                        borderColor: '#0BB140',
                                                                        '&:hover': {
                                                                            backgroundColor: '#0BB140',
                                                                            color: 'white',
                                                                        },
                                                                    }}
                                                                    onClick={() => handleAcceptCertificate(cert.title)}
                                                                >
                                                                    ✓
                                                                </Button>
                                                            )}
                                                            {isAccepted && (
                                                                <Chip
                                                                    icon={<CheckCircle />}
                                                                    label="✓"
                                                                    size="small"
                                                                    onClick={
                                                                        isReadOnlyForCurrentUser
                                                                            ? undefined
                                                                            : () =>
                                                                                  handleDeselectCertificate(cert.title)
                                                                    }
                                                                    sx={{
                                                                        backgroundColor: '#0BB140',
                                                                        color: 'white',
                                                                        cursor: isReadOnlyForCurrentUser
                                                                            ? 'default'
                                                                            : 'pointer',
                                                                        '&:hover': isReadOnlyForCurrentUser
                                                                            ? {}
                                                                            : { backgroundColor: '#0a9e38' },
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {expandedForm && (
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Neues Zertifikat hinzufügen
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Autocomplete
                                    freeSolo
                                    options={availableCertificates.map((cert) => cert.title)}
                                    value={inlineForm.name}
                                    onChange={(_, newValue) => {
                                        setInlineForm((prev) => ({ ...prev, name: newValue || '' }));
                                        setError('');
                                    }}
                                    onInputChange={(_, newInputValue) => {
                                        setInlineForm((prev) => ({ ...prev, name: newInputValue }));
                                        setError('');
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Zertifikat auswählen oder eingeben"
                                            size="small"
                                            error={!!error}
                                            helperText={error}
                                        />
                                    )}
                                />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleAddCertificate}
                                        disabled={!inlineForm.name || loading}
                                        startIcon={loading ? <CircularProgress size={16} /> : null}
                                    >
                                        {loading ? 'Hinzufügen...' : 'Hinzufügen'}
                                    </Button>
                                    <Button variant="outlined" size="small" onClick={() => setExpandedForm(false)}>
                                        Abbrechen
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {!isReadOnlyForCurrentUser && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            {!isManualMode && (secureState?.analysis.certificates.status === EAnalysisStatus.COMPLETED || secureState?.analysis.certificates.status === EAnalysisStatus.ERROR) && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={
                                        isRegenerating || cvAnalysis.state.isAnalyzing ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <Refresh />
                                        )
                                    }
                                    onClick={async () => {
                                        setIsRegenerating(true);
                                        try {
                                            await regenerateCertificates();
                                        } catch (error) {
                                            console.error('Failed to regenerate certificates:', error);
                                        } finally {
                                            setTimeout(() => setIsRegenerating(false), 5000);
                                        }
                                    }}
                                    disabled={cvAnalysis.state.isAnalyzing || isRegenerating}
                                    sx={{
                                        color: theme.palette.primary.main,
                                        borderColor: theme.palette.primary.main,
                                        '&:hover': { backgroundColor: theme.palette.primary.main, color: 'white' },
                                    }}
                                >
                                    {isRegenerating || cvAnalysis.state.isAnalyzing
                                        ? 'Generiere...'
                                        : 'Zertifikate neu analysieren'}
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={expandedForm ? <Close /> : <Add />}
                                onClick={() => setExpandedForm(!expandedForm)}
                                sx={{
                                    color: theme.palette.primary.main,
                                    borderColor: theme.palette.primary.main,
                                    '&:hover': { backgroundColor: theme.palette.primary.main, color: 'white' },
                                }}
                            >
                                {expandedForm ? 'Schließen' : 'Hinzufügen'}
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    const renderEditContent = () => (
        <>
            <RowElement title="Label und Beschreibung ausblenden">
                <input
                    type="checkbox"
                    checked={properties.hideLabels ?? false}
                    onChange={(e) =>
                        props.onEdit?.({
                            ...props.component,
                            properties: {
                                ...properties,
                                hideLabels: e.target.checked,
                            },
                        })
                    }
                />
            </RowElement>
        </>
    );

    const renderViewContent = () => {
        const { state } = cvAnalysis;

        if (state.isAnalyzing) {
            return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CardMembership sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Analysiere Zertifikate...
                    </Typography>
                    <LinearProgress sx={{ maxWidth: 300, mx: 'auto' }} />
                </Box>
            );
        }

        if (state.error) {
            return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        <Typography variant="body1">{state.error}</Typography>
                    </Alert>
                </Box>
            );
        }

        return <Box sx={{ width: '100%' }}>{renderAnalysisResults()}</Box>;
    };

    return (
        <ETTBaseTemplateComponent
            {...props}
            renderEditContent={renderEditContent}
            renderViewContent={renderViewContent}
            hideLabels={properties.hideLabels}
        />
    );
};

export default ETTCertificateSelectionComponent;
